import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    ShieldAlert, ShieldCheck, Star, Coins, Handshake, MessageSquare,
    Wallet, Users, Tag, FileBarChart2, Building2, Gavel, Wrench, Zap, LifeBuoy, Bot, Inbox, TrendingUp,
    ArrowUp, ArrowDown, CreditCard, Landmark, Bike, Mail,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import AdminNewsletterGenerator from "../components/admin/AdminNewsletterGenerator";
// KnowledgeMailCard → verschoben auf /admin/hsn-tsn (KBA-Datenbank), Jul-2026
import { AdminUniversalSearch } from "../components/admin/AdminUniversalSearch";
import { AdminEmailPanel } from "../components/admin/AdminEmailPanel";
import { toast } from "sonner";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

// Lesbare Namen für die theme-Quellen der Seite „Was kann TwoWheels?".
const DEFAULT_MIN_CLICKS = 5; // Default-Schwelle (im Widget konfigurierbar, in localStorage)
const THEME_LABELS = {
    marktplatz: "Marktplatz & Suche", inserieren: "Inserieren & KI-Assistenz",
    crm: "CRM & Leads", werkstatt: "Werkstatt & Service", rechnungen: "Rechnungen",
    buchhaltung: "Buchhaltung & DATEV", wms: "Lager & Logistik (WMS)",
    disposition: "Disposition & Beschaffung", bewertung: "Fahrzeugbewertung",
    team: "Multi-Standort & Team", marketing: "Marketing & Shop",
    vermittler: "Vermittler", dienstleister: "Dienstleister", portale: "Kunden-Portale",
    ki: "KI-Plattform", admin: "Administration", konto: "Konto & Sicherheit",
};

/**
 * Phase 3.55 — Admin „At-a-Glance" Tile-View.
 *
 * Zentrales Landing für Admins: zeigt offene Aufgaben mit roten Badges
 * und Gesamtplattform-Zahlen, dazu schnelle Sprung-Kacheln zu allen
 * Management-Bereichen. Verlinkt von Header-Logo und Dropdown.
 */
export default function AdminDashboardPage() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [overview, setOverview] = useState(null);
    const [criticalChanges, setCriticalChanges] = useState(null);
    const [newLeads, setNewLeads] = useState(0);
    const [conv, setConv] = useState(null);
    const [convSort, setConvSort] = useState("count");
    const [convDays, setConvDays] = useState(90);
    const [convLoading, setConvLoading] = useState(true);
    const [minClicks, setMinClicks] = useState(() => {
        const s = parseInt(localStorage.getItem("conv_min_clicks"), 10);
        return Number.isFinite(s) && s > 0 ? s : DEFAULT_MIN_CLICKS;
    });
    const [loadingOverview, setLoadingOverview] = useState(true);

    // Schwelle in localStorage spiegeln, damit sie über Sessions erhalten bleibt.
    useEffect(() => {
        localStorage.setItem("conv_min_clicks", String(minClicks));
    }, [minClicks]);

    useEffect(() => {
        if (loading) return;
        if (!user || user === false || user.role !== "admin") {
            navigate("/");
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const [ov, cc, lds] = await Promise.all([
                    api.get("/admin/overview"),
                    api.get("/admin/dealers/critical-changes?since_hours=168&limit=10"),
                    api.get("/admin/leads?status=new&limit=1").catch(() => ({ data: { counts: {} } })),
                ]);
                if (cancelled) return;
                setOverview(ov.data);
                setCriticalChanges(cc.data);
                setNewLeads(lds.data?.counts?.new ?? 0);
            } catch (e) {
                console.error("Admin overview load failed", e);
            } finally {
                if (!cancelled) setLoadingOverview(false);
            }
        })();
        return () => { cancelled = true; };
    }, [user, loading, navigate]);

    // Conversion-Themen separat laden, damit der Zeitraum-Umschalter (7/30/90 Tage)
    // nur dieses Widget neu lädt und nicht das ganze Dashboard.
    useEffect(() => {
        if (loading || !user || user.role !== "admin") return;
        let cancelled = false;
        setConvLoading(true);
        (async () => {
            try {
                const cv = await api.get(`/admin/conversion-themes?days=${convDays}`)
                    .catch(() => ({ data: { themes: [], total: 0, days: convDays } }));
                if (!cancelled) setConv(cv.data);
            } finally {
                if (!cancelled) setConvLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [user, loading, convDays]);

    if (loading || !user) return <div className="p-12">Lädt...</div>;

    const pending = overview?.pending || {};
    const totals = overview?.totals || {};

    const pendingTiles = [
        { label: "Händler-Verifizierungen", icon: ShieldAlert, count: pending.dealers, to: "/admin/dealers?status=pending", testId: "admin-tile-pending-dealers", accent: "bg-red-50 border-red-300 text-red-900" },
        { label: "Vermittler-Freischaltung", icon: Users, count: pending.vermittlers, to: "/admin/vermittlers", testId: "admin-tile-pending-vermittlers", accent: "bg-orange-50 border-orange-300 text-orange-900" },
        { label: "Dienstleister-Anträge", icon: Wrench, count: pending.service_providers, to: "/admin/service-providers", testId: "admin-tile-pending-service-providers", accent: "bg-orange-50 border-orange-300 text-orange-900" },
        { label: "Bewertungen prüfen", icon: Star, count: pending.reviews, to: "/admin/reviews", testId: "admin-tile-pending-reviews", accent: "bg-yellow-50 border-yellow-300 text-yellow-900" },
        { label: "Compliance offen", icon: ShieldCheck, count: pending.compliance, to: "/admin/compliance", testId: "admin-tile-pending-compliance", accent: "bg-red-50 border-red-300 text-red-900" },
        { label: "Neue Leads (Autobörse)", icon: Inbox, count: newLeads, to: "/admin/leads", testId: "admin-tile-pending-leads", accent: "bg-amber-50 border-amber-300 text-amber-900" },
    ];

    const managementTiles = [
        { label: "Eingehende Leads", icon: Inbox, to: "/admin/leads", testId: "admin-tile-leads" },
        { label: "Interessensanfragen (Teaser)", icon: Inbox, to: "/admin/contact-leads", testId: "admin-tile-contact-leads" },
        { label: "Händler-Verwaltung", icon: ShieldAlert, to: "/admin/dealers", testId: "admin-tile-dealers" },
        { label: "Compliance-Übersicht", icon: ShieldCheck, to: "/admin/compliance", testId: "admin-tile-compliance" },
        { label: "Vermittler", icon: Users, to: "/admin/vermittlers", testId: "admin-tile-vermittlers" },
        { label: "Konten-Werkzeuge", icon: Users, to: "/admin/konten", testId: "admin-tile-account-tools" },
        { label: "Betreiberdaten", icon: Landmark, to: "/admin/betreiber", testId: "admin-tile-operator" },
        { label: "Newsletter", icon: Inbox, to: "/admin/newsletter", testId: "admin-tile-newsletter" },
        { label: "Dienstleister", icon: Wrench, to: "/admin/service-providers", testId: "admin-tile-service-providers" },
        { label: "Bewertungs-Moderation", icon: Star, to: "/admin/reviews", testId: "admin-tile-reviews" },
        { label: "€Punkte-Verwaltung", icon: Coins, to: "/admin/points", testId: "admin-tile-points" },
        { label: "Wallet-Verwaltung", icon: Wallet, to: "/admin/wallet", testId: "admin-tile-wallet" },
        { label: "Pfand & Refinanzierung", icon: Coins, to: "/admin/pawn", testId: "admin-tile-pawn" },
        { label: "Tarife & Pakete", icon: Tag, to: "/admin/tariffs", testId: "admin-tile-tariffs" },
        { label: "SEPA-Abrechnung (Kosten)", icon: CreditCard, to: "/admin/usage-billing", testId: "admin-tile-usage-billing" },
        { label: "PStTG / DAC7-Meldung", icon: Landmark, to: "/admin/psttg", testId: "admin-tile-psttg" },
        { label: "Gemeldete Angebote (DSA)", icon: ShieldAlert, to: "/admin/listing-reports", testId: "admin-tile-reports" },
        { label: "Top-Inserat-Tarif", icon: Zap, to: "/admin/top-listing", testId: "admin-tile-top-listing" },
        { label: "SMS-Verlauf", icon: MessageSquare, to: "/admin/sms/history", testId: "admin-tile-sms" },
        { label: "Support-Tickets", icon: LifeBuoy, to: "/admin/tickets", testId: "admin-tile-tickets" },
        { label: "Assistenz-Nutzung", icon: Bot, to: "/admin/assistant-stats", testId: "admin-tile-assistant-stats" },
        { label: "E-Mail-Gesundheit", icon: Inbox, to: "/admin/email-health", testId: "admin-tile-email-health" },
        { label: "Hilfe-Übersicht", icon: LifeBuoy, to: "/admin/hilfe-uebersicht", testId: "admin-tile-help" },
        { label: "Fahrzeug-Kategorien", icon: Bike, to: "/admin/system/categories", testId: "admin-tile-system-categories" },
        { label: "KBA-Datenbank & KI-Wissen", icon: Bot, to: "/admin/hsn-tsn", testId: "admin-tile-hsn-tsn" },
        { label: "Systempflege (Gefahrenzone)", icon: ShieldAlert, to: "/admin/systempflege", testId: "admin-tile-maintenance" },
    ];

    const totalsCards = [
        { label: "Inserate", icon: Gavel, value: totals.listings, testId: "admin-stat-listings" },
        { label: "B2B-Händler", icon: Building2, value: totals.b2b, testId: "admin-stat-b2b" },
        { label: "B2C-Nutzer", icon: Users, value: totals.b2c, testId: "admin-stat-b2c" },
        { label: "Vermittler", icon: Handshake, value: totals.vermittlers, testId: "admin-stat-vermittlers" },
        { label: "Dienstleister", icon: Wrench, value: totals.service_providers, testId: "admin-stat-service-providers" },
    ];

    return (
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12" data-testid="admin-dashboard-page">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-2">
                <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-gray-500">Admin · At-a-Glance</div>
                    <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">Übersicht</h1>
                </div>
                <div className="text-xs text-gray-500">Eingeloggt als <strong className="text-[#111827]">{user.name}</strong></div>
            </div>
            <p className="text-gray-600 mb-4">Alle offenen Aufgaben und Management-Bereiche auf einen Blick.</p>

            {/* === Jul-2026 (User-Wunsch) — Universalsuche über Konten + Inserate === */}
            <AdminUniversalSearch />

            {/* === Jul-2026 (User-Wunsch) — E-Mail gebündelt: Freigaben + Center + Gesundheit === */}
            <AdminEmailPanel />

            {/* === Pending Aufgaben (rote/orange Aufmerksamkeits-Kacheln) === */}
            <section className="mb-10" data-testid="admin-pending-section">
                <h2 className="font-display text-lg font-black mb-3 flex items-center gap-2">
                    <span className="inline-block w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                    Offene Aufgaben
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                    {pendingTiles.map((t) => {
                        const Icon = t.icon;
                        const hasWork = typeof t.count === "number" && t.count > 0;
                        return (
                            <Link
                                key={t.testId}
                                to={t.to}
                                data-testid={t.testId}
                                className={`relative block rounded-sm border-2 p-4 transition-all hover:-translate-y-0.5 hover:shadow-md ${hasWork ? t.accent : "bg-white border-gray-200 text-gray-600"}`}
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <Icon className="w-5 h-5" />
                                    {hasWork ? (
                                        <span className="inline-flex items-center justify-center min-w-[26px] h-6 px-1.5 rounded-sm bg-red-500 text-white text-sm font-black font-mono-num" data-testid={`${t.testId}-count`}>
                                            {loadingOverview ? "…" : t.count}
                                        </span>
                                    ) : (
                                        <span className="text-xs font-bold text-gray-400">—</span>
                                    )}
                                </div>
                                <div className="text-xs font-bold uppercase tracking-wider leading-tight">{t.label}</div>
                            </Link>
                        );
                    })}
                </div>
            </section>

            {/* === Phase 3.61 — Compliance-Pulse: kritische Stammdaten-Änderungen letzte 7 Tage === */}
            <section className="mb-10" data-testid="admin-critical-changes-section">
                <h2 className="font-display text-lg font-black mb-3 flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-amber-500" />
                    Compliance-Pulse · letzte 7 Tage
                    {criticalChanges?.count > 0 && (
                        <span className="inline-flex items-center justify-center min-w-[22px] h-6 px-2 bg-amber-500 text-white text-xs font-black font-mono-num rounded-sm" data-testid="admin-critical-changes-count">
                            {criticalChanges.count}
                        </span>
                    )}
                </h2>
                {loadingOverview ? (
                    <div className="bg-white border border-gray-200 rounded-sm p-6 text-sm text-gray-500">Lädt …</div>
                ) : (criticalChanges?.items?.length || 0) === 0 ? (
                    <div className="bg-white border border-gray-200 rounded-sm p-6 text-sm text-gray-500" data-testid="admin-critical-changes-empty">
                        Keine kritischen Stammdaten-Änderungen in den letzten 7 Tagen.
                    </div>
                ) : (
                    <div className="bg-white border border-gray-200 rounded-sm divide-y divide-gray-100" data-testid="admin-critical-changes-list">
                        {criticalChanges.items.slice(0, 8).map((item) => {
                            const d = item.dealer || {};
                            const fields = (item.changed_fields || []).join(", ");
                            const when = item.created_at ? new Date(item.created_at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
                            return (
                                <Link
                                    key={item.id}
                                    to={`/admin/dealers/${d.id || ""}/audit`}
                                    className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
                                    data-testid={`admin-critical-change-${item.id}`}
                                >
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm font-bold text-[#111827] truncate">{d.company_name || d.name || d.email || "Unbekannter Händler"}</div>
                                        <div className="text-[11px] text-gray-500 truncate">{fields || "—"}</div>
                                    </div>
                                    <div className="text-[10px] font-mono-num text-gray-400 whitespace-nowrap ml-3">{when}</div>
                                </Link>
                            );
                        })}
                    </div>
                )}
            </section>

            {/* === Plattform-Zahlen === */}
            <section className="mb-10" data-testid="admin-totals-section">
                <h2 className="font-display text-lg font-black mb-3">Plattform-Kennzahlen</h2>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    {totalsCards.map((c) => {
                        const Icon = c.icon;
                        return (
                            <div key={c.testId} className="bg-white border border-gray-200 rounded-sm p-4" data-testid={c.testId}>
                                <div className="flex items-center justify-between mb-2">
                                    <Icon className="w-5 h-5 text-gray-400" />
                                </div>
                                <div className="font-display font-black text-2xl font-mono-num text-[#111827]">
                                    {loadingOverview ? "…" : (c.value ?? 0).toLocaleString("de-DE")}
                                </div>
                                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mt-0.5">{c.label}</div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* === Top-Conversion-Themen (Quelle „Was kann TwoWheels?") === */}
            <section className="mb-10" data-testid="admin-conversion-section">
                <div className="flex items-start justify-between gap-3 mb-1">
                    <h2 className="font-display text-lg font-black flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-[#FFCC00]" />
                        Top-Conversion-Themen
                    </h2>
                    <div className="flex flex-wrap items-center gap-2 shrink-0">
                        <div className="flex gap-1" data-testid="admin-conversion-days">
                            {[7, 30, 90].map((d) => (
                                <button
                                    key={d}
                                    onClick={() => setConvDays(d)}
                                    className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-sm border transition-colors ${convDays === d ? "bg-[#FFCC00] text-[#111827] border-[#FFCC00]" : "bg-white text-gray-600 border-gray-300 hover:border-[#111827]"}`}
                                    data-testid={`admin-conversion-days-${d}`}
                                >
                                    {d} T
                                </button>
                            ))}
                        </div>
                        <div className="flex gap-1" data-testid="admin-conversion-sort">
                            <button
                                onClick={() => setConvSort("count")}
                                className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-sm border transition-colors ${convSort === "count" ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-gray-600 border-gray-300 hover:border-[#111827]"}`}
                                data-testid="admin-conversion-sort-count"
                            >
                                Nach Anzahl
                            </button>
                            <button
                                onClick={() => setConvSort("rate")}
                                className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-sm border transition-colors ${convSort === "rate" ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-gray-600 border-gray-300 hover:border-[#111827]"}`}
                                data-testid="admin-conversion-sort-rate"
                            >
                                Nach Rate
                            </button>
                        </div>
                    </div>
                </div>
                <p className="text-xs text-gray-500 mb-3">
                    Registrierungen &amp; Demo-Button-Klicks nach Quell-Thema der Seite „Was kann TwoWheels?" — letzte {conv?.days ?? convDays} Tage
                    {conv ? ` · ${conv.total} Anmeldung${conv.total === 1 ? "" : "en"} / ${conv.total_clicks ?? 0} Klick${(conv.total_clicks ?? 0) === 1 ? "" : "s"}` : ""}. <span className="text-gray-400">▲/▼ = Veränderung ggü. Vorperiode.</span>
                </p>
                {convLoading ? (
                    <div className="bg-white border border-gray-200 rounded-sm p-6 text-sm text-gray-500">Lädt …</div>
                ) : !conv || (conv.themes || []).length === 0 ? (
                    <div className="bg-white border border-gray-200 rounded-sm p-6 text-sm text-gray-500" data-testid="admin-conversion-empty">
                        Noch keine getrackten Registrierungen über die Funktionsübersicht. Sobald sich Nutzer über einen
                        „Direkt ausprobieren"-Button anmelden, erscheinen hier die Top-Themen.
                    </div>
                ) : (
                    <div className="bg-white border border-gray-200 rounded-sm divide-y divide-gray-100" data-testid="admin-conversion-list">
                        {convSort === "rate" && (
                            <div className="flex items-center justify-between gap-2 px-3 py-2 text-[10px] text-gray-400 bg-gray-50" data-testid="admin-conversion-rate-hint">
                                <span>Rate erst ab {minClicks} Klick{minClicks === 1 ? "" : "s"} aussagekräftig — Themen mit weniger sind ausgegraut und stehen am Ende.</span>
                                <label className="flex items-center gap-1.5 shrink-0 text-gray-500">
                                    <span className="font-bold uppercase tracking-wider">Min. Klicks</span>
                                    <input
                                        type="number"
                                        min={1}
                                        value={minClicks}
                                        onChange={(e) => {
                                            const v = parseInt(e.target.value, 10);
                                            setMinClicks(Number.isFinite(v) && v > 0 ? v : 1);
                                        }}
                                        className="w-14 px-1.5 py-0.5 text-[11px] font-mono-num text-[#111827] border border-gray-300 rounded-sm focus:outline-none focus:border-[#111827]"
                                        data-testid="admin-conversion-min-clicks-input"
                                    />
                                </label>
                            </div>
                        )}
                        {(convSort === "rate"
                            ? [...(conv.themes || [])].sort((a, b) => {
                                // Themen mit zu wenigen Klicks ans Ende (Rate nicht aussagekräftig)
                                const am = (a.clicks || 0) >= minClicks ? 1 : 0;
                                const bm = (b.clicks || 0) >= minClicks ? 1 : 0;
                                if (am !== bm) return bm - am;
                                return ((b.rate ?? -1) - (a.rate ?? -1)) || (b.count - a.count);
                            })
                            : (conv.themes || [])
                        ).map((th, i) => {
                            const max = Math.max(1, ...(conv.themes || []).map((t) => t.count || 0));
                            const pct = Math.round((th.count / max) * 100);
                            const lowSample = convSort === "rate" && (th.clicks || 0) < minClicks;
                            const roleStr = Object.entries(th.roles || {})
                                .sort((a, b) => b[1] - a[1])
                                .map(([r, n]) => `${r}: ${n}`).join(" · ");
                            return (
                                <div key={th.theme} className={`flex items-center gap-3 p-3 ${lowSample ? "opacity-50" : ""}`} data-testid={`admin-conversion-row-${th.theme}`}>
                                    <div className="w-6 text-center font-display font-black text-gray-300 text-sm">{i + 1}</div>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="text-sm font-bold text-[#111827] truncate">{THEME_LABELS[th.theme] || th.theme}</div>
                                            <div className="flex items-center gap-2 shrink-0">
                                                {th.trend != null && th.trend !== 0 && (
                                                    <span className={`inline-flex items-center gap-0.5 text-[10px] font-bold ${th.trend > 0 ? "text-emerald-600" : "text-red-500"}`} data-testid={`admin-conversion-trend-${th.theme}`} title={`Vorperiode: ${th.prev_count ?? 0} Anmeldung${(th.prev_count ?? 0) === 1 ? "" : "en"}`}>
                                                        {th.trend > 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                                                        {Math.abs(th.trend)}
                                                    </span>
                                                )}
                                                {th.rate != null && (
                                                    lowSample ? (
                                                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-gray-100 text-gray-400" data-testid={`admin-conversion-rate-${th.theme}`} title={`Erst ab ${minClicks} Klicks aussagekräftig`}>
                                                            {th.rate}% · zu wenig
                                                        </span>
                                                    ) : (
                                                        <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-sm ${th.rate >= 20 ? "bg-emerald-100 text-emerald-800" : th.rate >= 5 ? "bg-amber-100 text-amber-800" : "bg-gray-100 text-gray-600"}`} data-testid={`admin-conversion-rate-${th.theme}`}>
                                                            {th.rate}%
                                                        </span>
                                                    )
                                                )}
                                                <span className="text-sm font-black font-mono-num text-[#111827]" data-testid={`admin-conversion-count-${th.theme}`}>{th.count}</span>
                                            </div>
                                        </div>
                                        <div className="h-1.5 bg-gray-100 rounded-sm mt-1.5 overflow-hidden">
                                            <div className="h-full bg-[#FFCC00]" style={{ width: `${pct}%` }} />
                                        </div>
                                        <div className="flex items-center justify-between gap-2 mt-1">
                                            {roleStr ? <div className="text-[10px] text-gray-400 truncate">{roleStr}</div> : <span />}
                                            <div className="text-[10px] text-gray-400 shrink-0" data-testid={`admin-conversion-clicks-${th.theme}`}>
                                                {th.count}/{th.clicks ?? 0} · {th.clicks ? "Anmeldungen/Klicks" : "noch keine Klicks"}
                                            </div>
                                        </div>
                                        {(th.sublinks || []).length > 0 && (
                                            <div className="text-[10px] text-gray-400 truncate mt-0.5" data-testid={`admin-conversion-sublinks-${th.theme}`} title="Meistgeklickte Öffnen-Unterlinks">
                                                <span className="font-bold text-gray-500">Meistgeöffnet:</span> {th.sublinks.map((s) => `${s.label} (${s.clicks})`).join(" · ")}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>

            {/* === Management-Bereiche === */}
            <section data-testid="admin-management-section">
                <h2 className="font-display text-lg font-black mb-3 flex items-center gap-2">
                    <FileBarChart2 className="w-5 h-5 text-[#FFCC00]" />
                    Management-Bereiche
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                    {managementTiles.map((t) => {
                        const Icon = t.icon;
                        return (
                            <Link
                                key={t.testId}
                                to={t.to}
                                data-testid={t.testId}
                                className="block bg-white border border-gray-200 rounded-sm p-4 hover:border-[#111827] hover:-translate-y-0.5 hover:shadow-md transition-all"
                            >
                                <Icon className="w-5 h-5 mb-2 text-gray-700" />
                                <div className="text-xs font-bold uppercase tracking-wider text-[#111827] leading-tight">{t.label}</div>
                            </Link>
                        );
                    })}
                </div>
            </section>

            {/* === Phase 4.16 — KI-Newsletter / Wochen-Digest === */}
            <section className="mt-10" data-testid="admin-newsletter-section">
                <AdminNewsletterGenerator />
            </section>
        </div>
    );
}

// Jul-2026 (User-Wunsch): Admin setzt ALLE Fahrzeug-Verkäufe/Reservierungen zurück (mit Bestätigung).
function SystemMaintenanceSection() {
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState(null);

    const runReset = async () => {
        setConfirmOpen(false);
        setRunning(true);
        try {
            const { data } = await api.post("/admin/reset-vehicle-sales");
            setResult(data);
            const delSum = Object.values(data.deleted || {}).reduce((a, b) => a + b, 0);
            toast.success(`Zurückgesetzt: ${data.listings_status_reset} Fahrzeuge wieder verfügbar, ${delSum} Verkaufs-Datensätze gelöscht`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Zurücksetzen fehlgeschlagen");
        } finally { setRunning(false); }
    };

    return (
        <section className="mt-10" data-testid="admin-maintenance-section">
            <h2 className="font-display text-lg font-black mb-3 flex items-center gap-2 text-red-700">
                <ShieldAlert className="w-5 h-5" />
                Systempflege · Gefahrenzone
            </h2>
            <div className="bg-white border-2 border-red-200 rounded-sm p-4 flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-sm font-bold text-[#111827]">Fahrzeug-Verkäufe zurücksetzen</div>
                    <p className="text-xs text-gray-600 mt-0.5 max-w-2xl">
                        Setzt ALLE Verkäufe &amp; Reservierungen (alle Händler) zurück: Fahrzeuge werden wieder
                        verfügbar, Verkaufsprozesse geleert, zugehörige Rechnungen, Belege, Transporte, Provisionen
                        und Rechnungszähler entfernt. Nicht umkehrbar!
                    </p>
                    {result && (
                        <p className="text-[11px] text-emerald-700 font-bold mt-1" data-testid="admin-reset-sales-result">
                            ✓ {result.listings_status_reset} Fahrzeuge wieder verfügbar · {result.listings_fields_cleared} Prozesse geleert · {Object.values(result.deleted || {}).reduce((a, b) => a + b, 0)} Datensätze gelöscht
                        </p>
                    )}
                </div>
                <button
                    onClick={() => setConfirmOpen(true)}
                    disabled={running}
                    className="shrink-0 px-4 py-2 rounded-sm bg-red-600 hover:bg-red-700 text-white text-xs font-bold uppercase tracking-wider disabled:opacity-50"
                    data-testid="admin-reset-sales-btn"
                >
                    {running ? "Läuft …" : "Verkäufe zurücksetzen"}
                </button>
            </div>
            <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <AlertDialogContent data-testid="admin-reset-sales-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Alle Fahrzeug-Verkäufe zurücksetzen?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Dies löscht bei ALLEN Händlern sämtliche Verkäufe, Reservierungen, Verkaufsprozesse,
                            Fahrzeug-Rechnungen und Belege. Die Fahrzeuge werden wieder als verfügbar angezeigt.
                            Diese Aktion kann NICHT rückgängig gemacht werden.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="admin-reset-sales-cancel">Abbrechen</AlertDialogCancel>
                        <AlertDialogAction onClick={runReset} className="bg-red-600 hover:bg-red-700 text-white" data-testid="admin-reset-sales-confirm">
                            Ja, alles zurücksetzen
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
            {/* Jun-2026 (User-Wunsch) — Test-Daten endgültig löschen */}
            <PurgeToolCard
                title="Alle Aufgaben endgültig löschen"
                description="Löscht ALLE Mitarbeiter- und Händler-Aufgaben (offen + erledigt, alle Händler) endgültig aus der Datenbank. Für Test-Resets vor dem Go-Live. Nicht umkehrbar!"
                endpoint="/admin/purge-all-tasks"
                confirmText="Wirklich ALLE Aufgaben (alle Händler, offen + erledigt) endgültig löschen?"
                testId="admin-purge-tasks"
            />
            <PurgeToolCard
                title="Test-Kunden löschen"
                description="Löscht Kunden wie „Max Mustermann“, Demo-/Testkunden und komplett namenlose Datensätze („Kunde —“) inkl. deren verknüpfter Fahrzeuge. Erst Vorschau prüfen!"
                endpoint="/admin/purge-test-customers"
                confirmText="Alle gefundenen Test-Kunden (inkl. verknüpfter Fahrzeuge) endgültig löschen?"
                testId="admin-purge-test-customers"
                withPreview
            />
            <PurgeToolCard
                title="Kundenfahrzeuge ohne FIN löschen"
                description="Löscht alle Kundenfahrzeuge (Garage), bei denen KEINE Fahrgestellnummer hinterlegt ist. Erst Vorschau prüfen!"
                endpoint="/admin/purge-vinless-customer-vehicles"
                confirmText="Alle Kundenfahrzeuge ohne FIN endgültig löschen?"
                testId="admin-purge-vinless"
                withPreview
            />
            <PurgeToolCard
                title="Werkstattaufträge zurücksetzen"
                description="Löscht ALLE Werkstattaufträge (alle Händler, alle Status) inkl. Zeitbuchungen endgültig und setzt die Auftragsnummern auf 0 zurück — der nächste Auftrag beginnt wieder bei …-00001. Erst Vorschau prüfen!"
                endpoint="/admin/reset-work-orders"
                confirmText="Wirklich ALLE Werkstattaufträge endgültig löschen und den Nummernkreis auf 0 zurücksetzen?"
                testId="admin-reset-workorders"
                withPreview
            />
        </section>
    );
}

// Jun-2026 (User-Wunsch) — kompakte Gefahrenzone-Karte mit optionaler Vorschau (dry_run).
function PurgeToolCard({ title, description, endpoint, confirmText, testId, withPreview = false }) {
    const [busy, setBusy] = useState(false);
    const [preview, setPreview] = useState(null);
    const [result, setResult] = useState(null);

    const loadPreview = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(endpoint, { dry_run: true });
            setPreview(data);
        } catch (e) { toast.error(e.response?.data?.detail || "Vorschau fehlgeschlagen"); }
        setBusy(false);
    };
    const run = async () => {
        if (!window.confirm(confirmText)) return;
        setBusy(true);
        try {
            const { data } = await api.post(endpoint, { dry_run: false });
            setResult(data);
            setPreview(null);
            toast.success(`${data.deleted ?? 0} Datensätze endgültig gelöscht${data.vehicles_deleted ? ` (+${data.vehicles_deleted} Fahrzeuge)` : ""}`);
        } catch (e) { toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen"); }
        setBusy(false);
    };
    return (
        <div className="bg-white border-2 border-red-200 rounded-sm p-4 mt-3" data-testid={`${testId}-card`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-sm font-bold text-[#111827]">{title}</div>
                    <p className="text-xs text-gray-600 mt-0.5 max-w-2xl">{description}</p>
                    {result && (
                        <p className="text-[11px] text-emerald-700 font-bold mt-1" data-testid={`${testId}-result`}>
                            ✓ {result.deleted ?? 0} gelöscht{result.vehicles_deleted ? ` · ${result.vehicles_deleted} verknüpfte Fahrzeuge entfernt` : ""}
                        </p>
                    )}
                </div>
                <div className="flex gap-2 shrink-0">
                    {withPreview && (
                        <button onClick={loadPreview} disabled={busy}
                            className="px-3 py-2 rounded-sm border border-gray-300 text-gray-700 text-xs font-bold uppercase tracking-wider hover:border-[#111827] disabled:opacity-50"
                            data-testid={`${testId}-preview-btn`}>
                            Vorschau
                        </button>
                    )}
                    <button onClick={run} disabled={busy || (withPreview && preview && preview.count === 0)}
                        className="px-4 py-2 rounded-sm bg-red-600 hover:bg-red-700 text-white text-xs font-bold uppercase tracking-wider disabled:opacity-50"
                        data-testid={`${testId}-run-btn`}>
                        {busy ? "Läuft …" : "Endgültig löschen"}
                    </button>
                </div>
            </div>
            {preview && (
                <div className="mt-2 border-t border-gray-100 pt-2" data-testid={`${testId}-preview`}>
                    <div className="text-xs font-bold text-red-700">{preview.count} Treffer würden gelöscht:</div>
                    <div className="text-[11px] text-gray-600 mt-1 max-h-32 overflow-y-auto">
                        {(preview.sample || []).map((s, i) => (
                            <div key={i}>{s.name}{s.customer_number ? ` · ${s.customer_number}` : ""}{s.email ? ` · ${s.email}` : ""}</div>
                        ))}
                        {preview.count > (preview.sample || []).length && <div className="text-gray-400">… und {preview.count - preview.sample.length} weitere</div>}
                    </div>
                </div>
            )}
        </div>
    );
}
